import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Shield, 
  Search, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Eye,
  Download,
  Share2,
  ExternalLink,
  LogOut,
  Building2,
  User,
  FileText,
  Calendar,
  Award,
  Globe,
  Mail,
  Phone,
  MapPin,
  ExternalLink as ExternalLinkIcon
} from 'lucide-react';

const mockVerificationHistory = [
  {
    id: '1',
    credentialTitle: 'Real Estate License',
    holderName: 'Jane Doe',
    issuer: 'National Real Estate Board',
    verifiedDate: '2024-03-20 14:30',
    status: 'valid',
    verificationMethod: 'URL',
    category: 'Real Estate'
  },
  {
    id: '2',
    credentialTitle: 'Certified Nursing Assistant (CNA)',
    holderName: 'Jane Doe',
    issuer: 'Healthcare Credentials Authority',
    verifiedDate: '2024-03-19 10:15',
    status: 'valid',
    verificationMethod: 'QR Code',
    category: 'Healthcare'
  },
  {
    id: '3',
    credentialTitle: 'Hunting License',
    holderName: 'Jane Doe',
    issuer: 'Department of Natural Resources',
    verifiedDate: '2024-03-18 16:45',
    status: 'valid',
    verificationMethod: 'URL',
    category: 'Hunting'
  },
  {
    id: '4',
    credentialTitle: 'Franchise Owner Certification',
    holderName: 'Jane Doe',
    issuer: 'Franchise Certification Board',
    verifiedDate: '2024-03-17 09:22',
    status: 'valid',
    verificationMethod: 'File Upload',
    category: 'Franchise'
  },
  {
    id: '5',
    credentialTitle: 'Certified Livestock Handler',
    holderName: 'Jane Doe',
    issuer: 'American Livestock Association',
    verifiedDate: '2024-03-16 11:30',
    status: 'valid',
    verificationMethod: 'QR Code',
    category: 'Livestock'
  }
];

export default function VerifierPortal() {
  const { user, logout } = useAuth();
  const [verificationUrl, setVerificationUrl] = useState('');
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationHistory, setVerificationHistory] = useState(mockVerificationHistory);
  const [historyFilter, setHistoryFilter] = useState('all');

  // Get the appropriate template for a credential
  const getCredentialTemplate = (credential) => {
    if (!credential.templateId) return null;
    
    // Find the template in any tenant's templates
    // This part of the logic needs to be adapted if templates are no longer mocked
    // For now, we'll return a placeholder or null if templates are removed
    return null; 
  };

  const handleVerification = async () => {
    if (!verificationUrl.trim()) return;
    
    setIsVerifying(true);
    
    // Mock verification process - randomly select a credential from our mock data
    setTimeout(() => {
      const randomCredential = mockVerificationHistory[Math.floor(Math.random() * mockVerificationHistory.length)];
      
      // Create a verification result based on the selected credential
      const result = {
        id: randomCredential.id,
        title: randomCredential.credentialTitle,
        holder: randomCredential.holderName,
        issuer: randomCredential.issuer,
        issuerLogo: 'https://via.placeholder.com/50', // Placeholder for issuer logo
        issuedDate: '2023-01-01', // Placeholder for issued date
        expiryDate: '2024-12-31', // Placeholder for expiry date
        status: randomCredential.status,
        verificationId: `VER-2024-${Math.floor(Math.random() * 999999)}`,
        category: randomCredential.category,
        fields: { // Placeholder for fields
          'Credential ID': `CRED-${Math.floor(Math.random() * 100000)}`,
          'Holder Name': randomCredential.holderName,
          'Issuer': randomCredential.issuer,
          'Issue Date': '2023-01-01',
          'Expiry Date': '2024-12-31',
          'Status': randomCredential.status,
          'Verification Method': randomCredential.verificationMethod
        },
        templateId: null, // Placeholder for templateId
        issuerInfo: {
          name: randomCredential.issuer,
          address: getIssuerAddress(randomCredential.category),
          website: getIssuerWebsite(randomCredential.issuer),
          accreditation: getIssuerAccreditation(randomCredential.category)
        }
      };
      
      setVerificationResult(result);
      setIsVerifying(false);
    }, 2000);
  };

  const getIssuerAddress = (category: string) => {
    const addresses = {
      'Education': '123 University Ave, College Town, ST 12345',
      'Real Estate': '456 Commerce St, Business City, CA 90210',
      'Healthcare': '789 Medical Plaza, Health City, TX 75001',
      'Livestock': '321 Ranch Road, Agriculture Town, NE 68001',
      'Franchise': '654 Business Blvd, Corporate City, NY 10001',
      'MLM': '987 Enterprise Ave, Network City, FL 33001',
      'Membership': '147 Association Dr, Member City, IL 60601',
      'Hunting': '258 Wildlife Way, Nature City, MT 59001',
      'Fishing': '369 Marina Dr, Lake City, WA 98001'
    };
    return addresses[category] || '123 Main St, Any City, ST 12345';
  };

  const getIssuerWebsite = (issuer: string) => {
    return `https://${issuer.toLowerCase().replace(/\s+/g, '')}.org`;
  };

  const getIssuerAccreditation = (category: string) => {
    const accreditations = {
      'Education': 'Accredited by Higher Learning Commission',
      'Real Estate': 'Licensed by State Real Estate Commission',
      'Healthcare': 'Certified by National Healthcare Board',
      'Livestock': 'Authorized by Department of Agriculture',
      'Franchise': 'Registered with Federal Trade Commission',
      'MLM': 'Compliant with FTC Direct Selling Guidelines',
      'Membership': 'Recognized Professional Association',
      'Hunting': 'Authorized by State Wildlife Department',
      'Fishing': 'Licensed by State Fish & Game Department'
    };
    return accreditations[category] || 'Certified by Appropriate Regulatory Body';
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleVerification(); // Same mock process
    }
  };

  const filteredHistory = historyFilter === 'all' 
    ? mockVerificationHistory 
    : mockVerificationHistory.filter(item => item.category === historyFilter);

  const categories = ['all', ...Array.from(new Set(mockVerificationHistory.map(item => item.category)))];

  return (
    <div className="min-h-screen bg-cool-gray-100">
      {/* Header */}
      <header className="credential-capital-header shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 p-2 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">
                  Credential Verifier
                </h1>
                <p className="text-blue-100">
                  Verify digital credentials instantly
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <span className="text-blue-100">Welcome, {user.name}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={logout}
                    className="credential-capital-button-header"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Verifications Today</p>
                    <p className="text-3xl font-bold text-charcoal-ink">47</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl">
                    <CheckCircle className="h-8 w-8 text-electric-steel" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Valid Credentials</p>
                    <p className="text-3xl font-bold text-charcoal-ink">45</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-xl">
                    <Shield className="h-8 w-8 text-verified-green" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Industries</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{categories.length - 1}</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl">
                    <Building2 className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Success Rate</p>
                    <p className="text-3xl font-bold text-charcoal-ink">95.7%</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-xl">
                    // QrCode icon was removed, so this placeholder is removed
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Verification Methods */}
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle className="text-xl font-display text-charcoal-ink">
                Verify Credential
              </CardTitle>
              <CardDescription className="text-warm-slate">
                Enter a verification URL, upload a credential file, or scan a QR code
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* URL Verification */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <ExternalLinkIcon className="h-5 w-5 text-electric-steel" />
                    <Label htmlFor="verification-url" className="text-base font-medium text-charcoal-ink">
                      Verification URL
                    </Label>
                  </div>
                  <div className="flex space-x-2">
                    <Input
                      id="verification-url"
                      placeholder="https://credentialcapital.co/credential/..."
                      value={verificationUrl}
                      onChange={(e) => setVerificationUrl(e.target.value)}
                      className="flex-1 credential-capital-input"
                    />
                    <Button 
                      onClick={handleVerification} 
                      disabled={isVerifying || !verificationUrl.trim()}
                      className="credential-capital-button-primary"
                    >
                      {isVerifying ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Verify
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-verified-green" />
                    <Label className="text-base font-medium text-charcoal-ink">
                      Upload Credential File
                    </Label>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".json,.pdf,.png,.jpg,.jpeg"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-warm-slate">
                        Click to upload or drag and drop a credential file
                      </p>
                      <p className="text-xs text-warm-slate mt-1">
                        Supports JSON, PDF, PNG, JPG files
                      </p>
                    </label>
                  </div>
                </div>

                {/* QR Code Scanner */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Share2 className="h-5 w-5 text-purple-600" />
                    <Label className="text-base font-medium text-charcoal-ink">
                      QR Code Scanner
                    </Label>
                  </div>
                  <Button variant="outline" className="w-full credential-capital-button-secondary">
                    <Share2 className="h-4 w-4 mr-2" />
                    Open Camera to Scan QR Code
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verification Result */}
          {verificationResult && (
            <Card className="credential-capital-card">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  {verificationResult.status === 'valid' ? (
                    <CheckCircle className="h-8 w-8 text-verified-green" />
                  ) : verificationResult.status === 'expired' ? (
                    <AlertTriangle className="h-8 w-8 text-yellow-600" />
                  ) : (
                    <Clock className="h-8 w-8 text-red-600" />
                  )}
                  <div>
                    <CardTitle className={`text-xl ${
                      verificationResult.status === 'valid' ? 'text-verified-green' :
                      verificationResult.status === 'expired' ? 'text-yellow-700' :
                      'text-red-700'
                    }`}>
                      {verificationResult.status === 'valid' ? 'Credential Verified' :
                       verificationResult.status === 'expired' ? 'Credential Expired' :
                       'Verification Failed'}
                    </CardTitle>
                    <CardDescription className="text-warm-slate">
                      Verification ID: {verificationResult.verificationId}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="ml-auto">
                    {verificationResult.category}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* PDF Preview */}
                  {verificationResult.templateId && (
                    <div className="flex justify-center">
                      <div className="border border-gray-300 rounded-lg overflow-hidden shadow-lg">
                        {/* CredentialPreview component was removed, so this placeholder is removed */}
                        <div className="p-6 text-center">
                          <p className="text-lg text-warm-slate">Credential Preview Placeholder</p>
                          <p className="text-sm text-warm-slate">This feature is under development.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Credential Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <User className="h-12 w-12 text-gray-400" />
                        <div>
                          <h3 className="font-semibold text-lg text-charcoal-ink">{verificationResult.title}</h3>
                          <p className="text-warm-slate">Holder: {verificationResult.holder}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Issued by:</span>
                          <span className="text-charcoal-ink">{verificationResult.issuer}</span>
                        </div>
                        <div className="flex justify-between p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Issue Date:</span>
                          <span className="text-charcoal-ink">{verificationResult.issuedDate}</span>
                        </div>
                        {verificationResult.expiryDate && (
                          <div className="flex justify-between p-3 bg-cool-gray-100 rounded-lg">
                            <span className="font-medium text-warm-slate">Expiry Date:</span>
                            <span className="text-charcoal-ink">{verificationResult.expiryDate}</span>
                          </div>
                        )}
                        <div className="flex justify-between p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Status:</span>
                          <Badge variant={
                            verificationResult.status === 'valid' ? 'default' :
                            verificationResult.status === 'expired' ? 'secondary' : 'destructive'
                          }>
                            {verificationResult.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-semibold text-charcoal-ink">Issuer Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Organization:</span>
                          <p className="text-charcoal-ink">{verificationResult.issuerInfo.name}</p>
                        </div>
                        <div className="p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Address:</span>
                          <p className="text-charcoal-ink">{verificationResult.issuerInfo.address}</p>
                        </div>
                        <div className="p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Website:</span>
                          <p className="text-electric-steel">{verificationResult.issuerInfo.website}</p>
                        </div>
                        <div className="p-3 bg-cool-gray-100 rounded-lg">
                          <span className="font-medium text-warm-slate">Accreditation:</span>
                          <p className="text-charcoal-ink">{verificationResult.issuerInfo.accreditation}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credential Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-charcoal-ink">Credential Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(verificationResult.fields).map(([key, value]) => (
                        <div key={key} className="p-3 bg-white rounded-lg border border-gray-200">
                          <span className="font-medium text-warm-slate block text-sm">{key}:</span>
                          <span className="text-charcoal-ink">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-4">
                    <Button variant="outline" className="credential-capital-button-secondary">
                      <FileText className="h-4 w-4 mr-2" />
                      Download Report
                    </Button>
                    <Button variant="outline" className="credential-capital-button-secondary">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                    <Button variant="outline" className="credential-capital-button-secondary">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Full Details
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setVerificationResult(null)}
                      className="credential-capital-button-secondary"
                    >
                      Verify Another
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Verification History (if user is logged in) */}
          {user && (
            <Card className="credential-capital-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-display text-charcoal-ink">
                      Recent Verifications
                    </CardTitle>
                    <CardDescription className="text-warm-slate">
                      Your credential verification history across all industries
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="history-filter" className="text-base font-medium text-warm-slate">Filter:</Label>
                    <select
                      id="history-filter"
                      value={historyFilter}
                      onChange={(e) => setHistoryFilter(e.target.value)}
                      className="credential-capital-input"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category === 'all' ? 'All Categories' : category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredHistory.map((verification) => (
                    <div key={verification.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <CheckCircle className="h-5 w-5 text-verified-green" />
                        <div>
                          <p className="font-medium text-charcoal-ink">{verification.credentialTitle}</p>
                          <p className="text-sm text-warm-slate">
                            {verification.holderName} • {verification.issuer}
                          </p>
                          <p className="text-xs text-warm-slate">
                            Verified via {verification.verificationMethod} • {verification.verifiedDate}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-xs">
                          {verification.category}
                        </Badge>
                        <Badge variant="default">
                          {verification.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Industry Information */}
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle className="text-xl font-display text-charcoal-ink">
                Supported Industries
              </CardTitle>
              <CardDescription className="text-warm-slate">
                Credential Capital supports verification across multiple industries
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl mb-2">🏠</div>
                  <h4 className="font-medium text-charcoal-ink">Real Estate</h4>
                  <p className="text-xs text-warm-slate">Licenses & Certifications</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl mb-2">🏥</div>
                  <h4 className="font-medium text-charcoal-ink">Healthcare</h4>
                  <p className="text-xs text-warm-slate">Medical Credentials</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl mb-2">🐄</div>
                  <h4 className="font-medium text-charcoal-ink">Livestock</h4>
                  <p className="text-xs text-warm-slate">Handler Certifications</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl mb-2">🏪</div>
                  <h4 className="font-medium text-charcoal-ink">Franchises</h4>
                  <p className="text-xs text-warm-slate">Business Memberships</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl mb-2">🦌</div>
                  <h4 className="font-medium text-charcoal-ink">Hunting</h4>
                  <p className="text-xs text-warm-slate">Licenses & Safety</p>
                </div>
                <div className="text-center p-4 bg-teal-50 rounded-lg">
                  <div className="text-2xl mb-2">🎣</div>
                  <h4 className="font-medium text-charcoal-ink">Fishing</h4>
                  <p className="text-xs text-warm-slate">Permits & Education</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}